import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import TractorImage from '../../../../assets/images/01-tractor.jpg';
import CombineImage from '../../../../assets/images/02-combine.jpg';
import TransplanterImage from '../../../../assets/images/03-rt.jpg';
import { MainHead } from '../../../../components/common/head/MainHead';
import { pageInfo, sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { Benefit } from './components/Benefit';
import { ContinueWithTempData } from './components/ContinueWithTempData';
import { FAQ } from './components/FAQ';
import { FreeOfCharge } from './components/FreeOfCharge';

export const SellTractorSurveyEntryPage = () => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  const handleMachineTypeButtonClick = (machineType: 'tractor' | 'combine' | 'transplanter') => {
    ampli.서베이시작버튼클릭({ '서베이 이름': '회원 판매 트랙터' });

    ampli.pvmClickSellMachineButton({
      buttonType: machineType,
      page_type: 'entry',
    });

    dispatch(userSellTractorActions.changeMachineType(machineType));

    navigate(`${sellTractorPageInfo.overviewPage.path}?type=${machineType}`, { replace: true });
  };

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        textLink={{
          text: '내 판매 농기계 관리하기',
          path: isUserLoggedIn ? pageInfo.myInfoSellTractorListPage.path : pageInfo.loginPage.path,
        }}
      />
    ),
    fixedFooter: <></>,
    fixedHeaderHeight: 48,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  useEffect(() => {
    dispatch(userSellTractorActions.resetState());
  }, [dispatch, userSellTractorActions]);

  return (
    <Container>
      <MainSection>
        <HeaderContaienr>
          <FreeOfCharge />
          <Benefit />
        </HeaderContaienr>
        <ContinueWithTempData />
        <ContentContainer>
          <ContentTitle>{'판매하실 농기계는 무엇인가요?'}</ContentTitle>
          <ButtonContainer>
            <MachineTypeButton onClick={() => handleMachineTypeButtonClick('tractor')}>
              <MachineTypeImage imageUrl={TractorImage} />
              <MachineTypeLabel>{'트랙터'}</MachineTypeLabel>
            </MachineTypeButton>
            <MachineTypeButton onClick={() => handleMachineTypeButtonClick('combine')}>
              <MachineTypeImage imageUrl={CombineImage} />
              <MachineTypeLabel>{'콤바인'}</MachineTypeLabel>
            </MachineTypeButton>
            <MachineTypeButton onClick={() => handleMachineTypeButtonClick('transplanter')}>
              <MachineTypeImage imageUrl={TransplanterImage} />
              <MachineTypeLabel>{'이앙기'}</MachineTypeLabel>
            </MachineTypeButton>
          </ButtonContainer>
        </ContentContainer>
      </MainSection>
      <FAQ />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  background-color: white;

  padding-bottom: 40px;
`;

const HeaderSection = styled.div`
  width: 100%;
  height: 36px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 8px 16px;

  gap: 16px;
`;

const BackButton = styled.button``;

const GoToMandatoryDataButtonWrap = styled.div`
  position: relative;
`;

const GoToMandatoryDataButton = styled.button`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
  text-align: right;

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const MachineTypeImage = styled.div<{ imageUrl: string }>`
  width: 52px;
  height: 44.72px;

  background: ${(props) => `url(${props.imageUrl}) #F2f2f2 50% / cover no-repeat;`};
  background-blend-mode: darken;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: 32px var(--radius-large, 16px);
  gap: 24px;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const HeaderContaienr = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const ContentTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const MachineTypeButton = styled.button`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const MachineTypeLabel = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
